export interface MsalAccount {
    username: string;
    [key: string]: unknown;
}
export interface DomainConfig {
  display_name: string;
  sagemaker_domain?: string;
}
/**
 * Retrieves the username of the currently logged-in user from localStorage.
 * @returns {string} The username of the logged-in user, or an empty string if not found or on error.
 */
export const getLoggedInUser = (): string => {
    try {
        const accKeysJson = localStorage.getItem("msal.account.keys");
        if (!accKeysJson) return "";

        const accKeys: string[] = JSON.parse(accKeysJson);
        const firstKey = accKeys[0];
        if (!firstKey) return "";

        const userDataJson = localStorage.getItem(firstKey);
        if (!userDataJson) return "";

        const userData: MsalAccount = JSON.parse(userDataJson);
        return userData.username ?? "";
    } catch (error) {
        console.error("Failed to get logged-in user:", error);
        return "";
    }
};

/**
 * Safely parses a JSON string into an array.
 * Returns an empty array if the input is falsy, not valid JSON, or not an array.
 * @param {string | undefined | null} str - The JSON string to parse.
 * @returns {any[]} The parsed array, or an empty array on failure.
 */
export const safeParseArray = (str: string | undefined | null): any[] => {
  if (!str) return [];
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Retrieves the Sagemaker domain associated with a given display name from localStorage.
 * 
 * @param {string} domainName - The display name of the domain to look up
 * @returns {string|null} The Sagemaker domain if found, or null if:
 *   - No domain data exists in localStorage
 *   - The specified domain name doesn't exist in the data
 *   - The domain doesn't have a Sagemaker domain configured
 *   - An error occurs during retrieval or parsing
 */
export function getSagemakerDomainByDisplayName(domainName: string): string | null {
  try {
    const raw = localStorage.getItem("domainsData");
    if (!raw) return null;
    const arr: DomainConfig[] = JSON.parse(raw);
    const found = arr.find((item) => item.display_name === domainName);
    return found?.sagemaker_domain || null;
  } catch {
    return null;
  }
}

/**
 * Returns the syntax highlighting language for a given filename based on its extension.
 * Used for code preview components (e.g., react-syntax-highlighter).
 *
 * @param {string} filename - The name of the file (e.g., "example.py", "script.js").
 * @returns {string} The language string for syntax highlighting (e.g., "python", "javascript").
 */
export const getLanguageFromExtension = (filename: string): string => {
  const ext = filename.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "js": return "javascript";
    case "ts": return "typescript";
    case "jsx": return "jsx";
    case "tsx": return "tsx";
    case "py": return "python";
    case "java": return "java";
    case "c": return "c";
    case "cpp": return "cpp";
    case "cs": return "csharp";
    case "html": return "html";
    case "css": return "css";
    case "scss": return "scss";
    case "json": return "json";
    case "md": return "markdown";
    case "sh": return "bash";
    case "yaml":
    case "yml": return "yaml";
    case "xml": return "xml";
    case "sql": return "sql";
    default: return "text";
  }
};